<template>
  <cal-dialog-shell :initializing="loading || !employee || !employeeId" :subtitle="subtitle" :value="value" cancel-text="Schließen"
                    hide-save icon="mdi-account-tie-outline" title="" tone="info" @input="$emit('input', false)">
    <template #title-extra>
      <span v-if="!employee">Mitarbeiter:in</span>
      <template v-else>
        <span v-can:read-employees="{ id: employeeId, keys: ['gender'] }" class="mr-1">{{ toSalutation(employee.gender) }}</span>
        <span v-can:read-employees="{ id: employeeId, keys: ['firstName'] }" class="mr-1">{{ employee.firstName }}</span>
        <span v-can:read-employees="{ id: employeeId, keys: ['lastName'] }">{{ employee.lastName }}</span>
      </template>
    </template>

    <div v-if="employee" class="pcd-fields">
      <employee-card-availabilities :employee="employee"/>

      <profile-field v-can:read-employees="{ id: employeeId, keys: ['contactInformation'] }" icon="mdi-card-account-phone-outline"
                     label="Kontakt">
        <contact-lines :lines="contactLines"/>
      </profile-field>

      <profile-field v-if="employee.address" v-can:read-employees="{ id: employeeId, keys: ['address'] }" icon="mdi-map-marker-outline"
                     label="Adresse">
        <div class="fld__value">{{ employee.address.street }} {{ employee.address.houseNumber }}</div>
        <div class="fld__value">{{ employee.address.postal }} {{ employee.address.city }}</div>
        <template #actions>
          <a :href="mapsLink" aria-label="In Google Maps öffnen" class="ibtn" rel="noopener" target="_blank" v-tip="'In Google Maps öffnen'">
            <v-icon>mdi-map-search-outline</v-icon>
          </a>
          <button aria-label="Adresse kopieren" class="ibtn" type="button" v-tip="'Adresse kopieren'" @click="copy(addressText, 'Adresse')">
            <v-icon>mdi-content-copy</v-icon>
          </button>
        </template>
      </profile-field>

      <profile-field v-if="employee.birthday" v-can:read-employees="{ id: employee._id, keys: ['birthday'] }" icon="mdi-cake-variant-outline"
                     label="Geburtstag">
        <div class="fld__value">{{ moment(employee.birthday).format('DD. MMMM YYYY') }}</div>
        <div class="fld__note">{{ moment().diff(employee.birthday, 'years') }} Jahre alt</div>
      </profile-field>

      <template v-if="employee.isTeacher">
        <profile-field v-if="employee.subjects" v-can:read-employees="{ id: employee._id, keys: ['subjects'] }" icon="mdi-guitar-acoustic"
                       :label="employee.subjects.length > 1 ? 'Unterrichtsfächer' : 'Unterrichtsfach'">
          <div v-if="!employee.subjects.length" class="fld__value fld__value--muted">Keine Unterrichtsfächer hinterlegt</div>
          <div v-else class="chips">
            <span v-for="(subject, i) in employee.subjects" :key="i" class="chip">{{ subject.name ? subject.name : 'Unbekanntes Unterrichtsfach' }}</span>
          </div>
        </profile-field>

        <profile-field v-if="hasTravelInfos" icon="mdi-car-outline" label="Mobilität">
          <div class="fld__value">
            <v-icon class="mr-1" small>{{ employee.hasDriverLicense ? 'mdi-car' : 'mdi-car-off' }}</v-icon>{{ travelText }}
          </div>
          <div class="fld__note">Maximaler Fahrtweg: {{ employee.maxDistance ? `${employee.maxDistance} km` : 'Unbekannt' }}</div>
          <div class="fld__note">Maximale Fahrtdauer: {{ employee.maxTravelTime ? `${employee.maxTravelTime} min` : 'Unbekannt' }}</div>
        </profile-field>

        <profile-field v-if="employee.abilities" v-can:read-employees="{ id: employee._id, keys: ['abilities'] }" icon="mdi-head-lightbulb-outline"
                       label="Unterrichtsformen">
          <div class="chips">
            <span :class="employee.abilities.online ? 'tone-good' : 'tone-muted'" class="tag tag--sm" v-tip="onlineText">
              <v-icon x-small>mdi-cast-education</v-icon>Onlineunterricht
            </span>
            <span :class="employee.abilities.atHome ? 'tone-good' : 'tone-muted'" class="tag tag--sm" v-tip="atHomeText">
              <v-icon x-small>mdi-home-account</v-icon>Einzelunterricht zuhause
            </span>
          </div>
        </profile-field>
      </template>

      <employee-card-institutions v-if="employee.isTeacher" :employee="employee"/>
    </div>

    <template #footer-start>
      <button v-if="employee" v-can:custom-shareAccess class="cta cta--ghost" type="button"
              v-tip="`${employee.isTeacher ? 'Lehrkraft' : 'Mitarbeiter:in'} freigeben`" @click="shareAccess">
        <v-icon class="cta__icon" small>mdi-shield-lock-open</v-icon>Freigeben
      </button>
    </template>
  </cal-dialog-shell>
</template>


<script lang="ts">
import Vue from 'vue';
import moment from 'moment';
import { toSalutation } from '@/helpers/toSalutation.helper';
import { CrudEntityTypes } from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import { EmployeeHandlerAPI } from '@/classes/api/employee-handler.api.class';
import { Employee } from '@/interfaces/employee.interface';
import CalDialogShell from '@/components/timetable2/dialogs/CalDialogShell.vue';
import ProfileField from '@/components/common/profileUi/ProfileField.vue';
import ContactLines from '@/components/common/profileUi/ContactLines.vue';
import { ContactLine, copyWithSnackbar } from '@/components/common/profileUi/profileUi.helper';
import EmployeeCardAvailabilities from './employeeCardAvailabilities.vue';
import EmployeeCardInstitutions from './employeeCardInstitutions.vue';
import { addressTextOf, contactLinesOf, mapsLinkOf } from './cardDialog.helper';
import mongoose from "mongoose";

export default Vue.extend({
  components: {
    CalDialogShell,
    ProfileField,
    ContactLines,
    EmployeeCardAvailabilities,
    EmployeeCardInstitutions,
  },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    employeeId: {
      type: Object as () => mongoose.Types.ObjectId,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    employee: null as null | Employee,
  }),
  computed: {
    toSalutation: () => toSalutation,
    moment: () => moment,
    subtitle(): string {
      if (!this.employee) {
        return '';
      }
      const e = this.employee;
      const role = e.isEmployee && e.isTeacher ? 'Mitarbeiter:in und Lehrkraft' : e.isEmployee && !e.isTeacher ? 'Mitarbeitender' : 'Lehrkraft';
      return e.customId ? `${role} - ${e.customId}` : role;
    },
    contactLines(): ContactLine[] {
      return contactLinesOf(this.employee?.contactInformation);
    },
    addressText(): string {
      return addressTextOf(this.employee?.address);
    },
    mapsLink(): string {
      return mapsLinkOf(this.addressText);
    },
    hasTravelInfos(): boolean {
      const e = this.employee;
      return !!e && !!(e.hasDriverLicense || e.hasCar || e.maxDistance || e.maxTravelTime);
    },
    travelText(): string {
      const e = this.employee;
      if (!e) {
        return '';
      }
      return e.hasDriverLicense && e.hasCar ? 'Führerschein und Fahrzeug vorhanden'
        : e.hasDriverLicense && !e.hasCar ? 'Kein Fahrzeug vorhanden' : 'Kein Führerschein vorhanden';
    },
    onlineText(): string {
      const online = this.employee?.abilities?.online;
      return typeof online === 'boolean' ? `Die Lehrkraft gibt ${online ? '' : 'keinen'} Onlineunterricht.`
        : 'Es existieren keine Informationen über den Onlineunterricht.';
    },
    atHomeText(): string {
      const atHome = this.employee?.abilities?.atHome;
      return typeof atHome === 'boolean' ? `Die Lehrkraft gibt ${atHome ? '' : 'keinen'} Einzelunterricht zuhause.`
        : 'Es existieren keine Informationen über den Einzelunterricht.';
    },
  },
  methods: {
    copy(text: string, label: string) {
      return copyWithSnackbar(this, text, label);
    },
    async getEmployee() {
      try {
        this.loading = true;
        this.employee = await EmployeeHandlerAPI.findById(this.employeeId, {
          populate: {
            assignedInstitutions: {
              fields: ['name'],
            },
            plannedInstitutions: {
              fields: ['name'],
            },
            subjects: {
              fields: ['name'],
            },
          },
        });
      } catch (e) {
        this.$emit('input', false);
        this.$$showSnackbar('Es ist ein Fehler aufgetreten.');
      } finally {
        this.loading = false;
      }
    },
    shareAccess() {
      if (this.employee) {
        this.$$shareAccessDialog.show(CrudEntityTypes.EMPLOYEE, this.employee._id, 'Lehrkraft freigeben');
      }
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v: boolean) {
        if (v) {
          this.getEmployee();
        }
      },
    },
  },
});
</script>

<style scoped>
/* Field rows full width like in the profile cards */
.pcd-fields {
  margin: 4px -20px -20px;
}
</style>
<style src="@/components/acquisitionList/ui/itemDetails.css"></style>
